import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import DemoRequestModal from '../../components/marketing/DemoRequestModal';
import TrustBadge from '../../components/marketing/TrustBadge';
import ScreenshotFrame from '../../components/marketing/ScreenshotFrame';
import { featureHighlights } from '../../data/marketing/features';
import { trackEvent } from '../../lib/analytics';
import {
  EyeIcon,
  ClipboardDocumentCheckIcon,
  UserGroupIcon,
  ChartBarIcon,
  ShieldCheckIcon,
  AcademicCapIcon,
  LockClosedIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

const iconMap: Record<string, React.FC<{ className?: string }>> = {
  eye: EyeIcon,
  'clipboard-document-check': ClipboardDocumentCheckIcon,
  'user-group': UserGroupIcon,
  'chart-bar': ChartBarIcon,
  sparkles: SparklesIcon,
};

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-50px' },
  transition: { duration: 0.5 },
};

const stagger = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

const HomePage: React.FC = () => {
  const [demoOpen, setDemoOpen] = useState(false);

  return (
    <>
      <Helmet>
        <title>Sprout — Nurture Every Child's Learning Journey</title>
        <meta name="description" content="Sprout is a CAPS-aligned observation and assessment platform for South African early childhood centres. Track development, share with parents, and meet compliance requirements." />
        <meta property="og:title" content="Sprout — Nurture Every Child's Learning Journey" />
        <meta property="og:description" content="CAPS-aligned observation and assessment platform for South African schools." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://sprout-ui-pi.vercel.app/" />
      </Helmet>

      {/* Hero */}
      <section className="relative pt-24 pb-16 lg:pt-32 lg:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-50 via-white to-secondary-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-900" />
        <div className="absolute top-0 right-0 w-1/2 h-full opacity-10">
          <div className="absolute top-20 right-20 w-72 h-72 bg-primary-400 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-40 w-96 h-96 bg-secondary-400 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-gray-900 dark:text-white leading-tight"
            >
              Nurture Every Child's{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-secondary-500">
                Learning Journey
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="mt-6 text-lg sm:text-xl text-gray-600 dark:text-gray-300 leading-relaxed"
            >
              The observation and assessment platform built for South African ECD centres.
              Capture development milestones, align to CAPS curriculum, and keep parents connected — all in one place.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4"
            >
              <Link to="/pricing" onClick={() => trackEvent('cta_clicked', { cta_name: 'hero_start_free', location: 'home_hero' })}>
                <Button variant="primary" size="lg">Start Free — Unlimited Children</Button>
              </Link>
              <Button
                variant="outline"
                size="lg"
                onClick={() => {
                  trackEvent('cta_clicked', { cta_name: 'hero_book_demo', location: 'home_hero' });
                  setDemoOpen(true);
                }}
              >
                Book a Demo
              </Button>
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="mt-4 text-sm text-gray-500 dark:text-gray-400"
            >
              Free forever. Unlimited children. No credit card required.
            </motion.p>
          </div>

          {/* Real dashboard screenshot (falls back to a simple placeholder if missing) */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mt-16 max-w-5xl mx-auto"
          >
            <ScreenshotFrame
              src="/screenshots/marketing/dashboard.png"
              alt="Sprout dashboard showing child counts, observations, assessments, and recent activity"
              url="app.sprout.education/dashboard"
              fallback={
                <div className="rounded-2xl bg-white dark:bg-gray-900 shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <div className="aspect-[16/9] bg-gradient-to-br from-primary-50 to-secondary-50 dark:from-gray-800 dark:to-gray-800 flex items-center justify-center">
                    <p className="text-gray-400 dark:text-gray-500 text-sm">Dashboard preview</p>
                  </div>
                </div>
              }
            />
          </motion.div>
        </div>
      </section>

      {/* Compliance / Trust Strip */}
      <section className="py-12 bg-primary-50 dark:bg-primary-900/20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-sm text-primary-700 dark:text-primary-300 font-medium uppercase tracking-wide mb-6">
            Built for South African schools
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            <TrustBadge
              icon={ShieldCheckIcon}
              label="POPIA Operator"
              description="Operator agreement, audit logs & data export ready"
            />
            <TrustBadge
              icon={AcademicCapIcon}
              label="CAPS Aligned"
              description="Pre-loaded Grade R–3 curriculum"
            />
            <TrustBadge
              icon={LockClosedIcon}
              label="Encrypted"
              description="TLS in transit, encrypted at rest"
            />
          </div>
        </div>
      </section>

      {/* Feature Highlights */}
      <section className="py-20 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-gray-900 dark:text-white">
              Everything you need to support early learning
            </h2>
            <p className="mt-4 text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              From capturing daily observations to generating CAPS-aligned reports, Sprout handles the admin so you can focus on the children.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {featureHighlights.map((feature, i) => {
              const Icon = iconMap[feature.icon];
              return (
                <motion.div
                  key={feature.title}
                  {...stagger}
                  transition={{ delay: i * 0.1 }}
                >
                  <Card className="h-full">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-xl flex items-center justify-center">
                        {Icon && <Icon className="w-6 h-6 text-primary-600" />}
                      </div>
                      <div>
                        <h3 className="text-lg font-display font-semibold text-gray-900 dark:text-white">{feature.title}</h3>
                        <p className="mt-2 text-gray-600 dark:text-gray-300 text-sm leading-relaxed">{feature.description}</p>
                        <ul className="mt-3 space-y-1">
                          {feature.details.slice(0, 3).map(d => (
                            <li key={d} className="text-sm text-gray-500 dark:text-gray-400 flex items-center">
                              <svg className="w-4 h-4 text-secondary-500 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              {d}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link to="/features">
              <Button variant="outline">See All Features</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Audience Segments */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-gray-900 dark:text-white">
              Built for everyone at your centre
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: 'Directors',
                emoji: '🏫',
                description: 'Oversee your entire centre from one dashboard. Manage staff, track compliance, and generate reports that matter.',
              },
              {
                title: 'Teachers',
                emoji: '📝',
                description: 'Capture observations in seconds, not hours. Link to CAPS outcomes with a tap and focus on what you do best — teaching.',
              },
              {
                title: 'Parents',
                emoji: '👨‍👩‍👧',
                description: 'Stay connected to your child\'s learning with a secure portal. See observations, assessments, and progress updates anytime.',
              },
            ].map((segment, i) => (
              <motion.div key={segment.title} {...stagger} transition={{ delay: i * 0.15 }}>
                <Card className="text-center h-full">
                  <div className="text-4xl mb-4">{segment.emoji}</div>
                  <h3 className="text-lg font-display font-semibold text-gray-900 dark:text-white">{segment.title}</h3>
                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{segment.description}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* AI-Assist (privacy-first, local LLM) */}
      <section className="py-20 bg-gradient-to-br from-secondary-50 to-primary-50 dark:from-secondary-900/20 dark:to-primary-900/20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 mb-4 rounded-full bg-white dark:bg-gray-900 border border-secondary-200 dark:border-secondary-800 text-xs font-medium text-secondary-700 dark:text-secondary-300">
              <SparklesIcon className="w-4 h-4" />
              AI-Assist · Privacy-first by design
            </div>
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-gray-900 dark:text-white">
              Help with the writing — without sending data to a third party
            </h2>
            <p className="mt-4 text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Sprout's AI runs on a local language model on the same server as the API. Children's
              observation text never leaves the operator's infrastructure — no OpenAI, no Anthropic, no
              third-party AI processor. POPIA-aligned by default.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                title: 'Polish a draft',
                description: 'Turn rough teacher notes into clear narrative observations without changing what was actually observed.',
              },
              {
                title: 'Suggest a CAPS outcome',
                description: 'Type an observation and the model suggests which CAPS Learning Outcome it best maps to — you approve before it saves.',
              },
              {
                title: 'Runs locally',
                description: 'Llama-3.1-8B via Ollama, on the same server as Sprout. No API calls to OpenAI, Anthropic, or any third party.',
              },
            ].map((item, i) => (
              <motion.div key={item.title} {...stagger} transition={{ delay: i * 0.1 }}>
                <Card className="h-full">
                  <h3 className="text-base font-display font-semibold text-gray-900 dark:text-white">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                    {item.description}
                  </p>
                </Card>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link to="/docs/curriculum/ai-suggestions" className="inline-flex items-center text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline">
              Read how it works &rarr;
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-primary-600 to-primary-700 dark:from-primary-700 dark:to-primary-800">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <motion.h2
            {...fadeInUp}
            className="text-3xl sm:text-4xl font-display font-bold text-white"
          >
            Ready to transform your ECD centre?
          </motion.h2>
          <motion.p
            {...fadeInUp}
            className="mt-4 text-lg text-primary-100"
          >
            Join centres across South Africa who are digitising their observation and assessment workflows with Sprout.
          </motion.p>
          <motion.div
            {...fadeInUp}
            className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Link to="/pricing" onClick={() => trackEvent('cta_clicked', { cta_name: 'footer_get_started_free', location: 'home_footer_cta' })}>
              <Button
                variant="secondary"
                size="lg"
                className="bg-white dark:bg-gray-900 text-primary-700 dark:text-primary-300 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                Get Started Free
              </Button>
            </Link>
            <Button
              variant="ghost"
              size="lg"
              className="text-white border-white/30 border hover:bg-white/10"
              onClick={() => {
                trackEvent('cta_clicked', { cta_name: 'footer_book_demo', location: 'home_footer_cta' });
                setDemoOpen(true);
              }}
            >
              Book a Demo
            </Button>
          </motion.div>
        </div>
      </section>

      <DemoRequestModal isOpen={demoOpen} onClose={() => setDemoOpen(false)} />
    </>
  );
};

export default HomePage;
